import { ogImageMeta } from "@/lib/seo/og-image";
import { notFound, useLoaderData } from "@tanstack/react-router";
import { LocaleLink } from "@/components/site/LocaleLink";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PostBody, extractToc } from "@/components/insights/PostBody";
import { TableOfContents } from "@/components/insights/TableOfContents";
import { RelatedServices } from "@/components/insights/RelatedServices";
import { GatedDownload } from "@/components/insights/GatedDownload";
import { PostByline } from "@/components/insights/PostByline";
import { isPostExcludedFromBg } from "@/content/posts/bg-exclusions";
import { getPost } from "@/lib/wp.functions";
import type { WpPost } from "@/server/wp.server";
import { useT } from "@/lib/i18n/I18nProvider";
import { urlFor } from "@/lib/seo/schema";
import { blogPostSchemas } from "@/lib/seo/page-schema";
import {
  blogMetaDescription,
  blogMetaTitle,
  canonicalSlugFor,
  stripHtml,
} from "@/lib/seo/blog-meta";


export function ArticlePage() {
  const { post } = useLoaderData({ strict: false }) as { post: WpPost };
  const { locale } = useT();
  const isBg = locale === "bg";
  const media = post._embedded?.["wp:featuredmedia"]?.[0];
  const term = post._embedded?.["wp:term"]?.[0]?.[0];
  const { html: contentHtml, toc } = extractToc(post.content.rendered);
  const related = post.relatedServices ?? [];
  const gated = post.gatedDownload ?? null;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-[1180px] px-6 py-16 lg:px-10 lg:py-24">
        <nav className="text-[12px] text-muted-foreground" aria-label="Breadcrumb">
          <LocaleLink to="/" className="hover:text-foreground">{isBg ? "Начало" : "Home"}</LocaleLink>
          <span className="px-2">/</span>
          <LocaleLink to="/blog" className="hover:text-foreground">{isBg ? "Блог" : "Blog"}</LocaleLink>
        </nav>

        <div className="lg:grid lg:grid-cols-[minmax(0,760px)_minmax(0,1fr)] lg:gap-12">
          <article className="min-w-0">
            {term && (
              <p className="mt-6 text-[13px] font-medium text-accent">{term.name}</p>
            )}
            <h1
              className="font-display mt-3 text-[clamp(1.9rem,3.4vw,2.8rem)] font-medium leading-[1.15] text-foreground"
            >
              {stripHtml(post.title.rendered)}
            </h1>
            <PostByline
              date={post.date}
              updated={post.showUpdatedDate ? post.modified : undefined}
              locale={isBg ? "bg" : "en"}
              className="mt-4"
            />

            {media?.source_url && post.featuredMediaPlacement !== "content" && (
              <div className="mt-8 aspect-[16/9] w-full overflow-hidden rounded-2xl border border-border/60 bg-secondary">
                <img
                  src={media.source_url}
                  alt={media.alt_text || stripHtml(post.title.rendered)}
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              </div>
            )}

            <div className="mt-10">
              <PostBody html={contentHtml} />
            </div>

            {gated && <GatedDownload resource={gated} postTitle={stripHtml(post.title.rendered)} />}

            <RelatedServices items={related} />

            <div className="mt-16 border-t border-border/60 pt-8">
              <LocaleLink to="/blog" className="text-[13px] font-medium text-accent underline underline-offset-2">
                {isBg ? "← Обратно към блога" : "← Back to Blog"}
              </LocaleLink>
            </div>
          </article>

          <aside className="hidden lg:block">
            <TableOfContents items={toc} />
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
